import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, Tag, Calendar } from 'lucide-react';
import { getArticleBySlug } from '../services/knowledgeArticleApi';
import SEO from '../components/SEO';

// ---------------------------------------------------------------------------
// Minimal Markdown renderer (headings, tables, code blocks, bold, lists)
// ---------------------------------------------------------------------------
function renderMarkdown(md: string): React.ReactNode[] {
  const lines = md.split('\n');
  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      nodes.push(
        <pre key={i} className="bg-slate-900 border border-slate-700 rounded-xl p-4 overflow-x-auto my-4">
          <code className={`text-sm text-slate-200 language-${lang}`}>{codeLines.join('\n')}</code>
        </pre>
      );
      i++;
      continue;
    }

    // Table (starts with |)
    if (line.startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // filter out separator rows (---)
      const headerRow = tableLines[0];
      const bodyRows = tableLines.slice(2);
      const parseCells = (row: string) =>
        row.split('|').slice(1, -1).map(c => c.trim());

      nodes.push(
        <div key={i} className="overflow-x-auto my-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-700">
                {parseCells(headerRow).map((h, hi) => (
                  <th key={hi} className="text-left px-3 py-2 text-slate-300 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri} className="border-b border-slate-800 hover:bg-slate-800/40">
                  {parseCells(row).map((cell, ci) => (
                    <td key={ci} className="px-3 py-2 text-slate-400">
                      {cell.startsWith('`') && cell.endsWith('`') ? (
                        <code className="text-xs bg-slate-800 text-cyan-300 px-1.5 py-0.5 rounded">
                          {cell.slice(1, -1)}
                        </code>
                      ) : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      nodes.push(<h3 key={i} className="text-lg font-bold text-white mt-6 mb-2">{line.slice(4)}</h3>);
      i++; continue;
    }
    if (line.startsWith('## ')) {
      nodes.push(<h2 key={i} className="text-xl font-bold text-white mt-8 mb-3 border-b border-slate-700 pb-2">{line.slice(3)}</h2>);
      i++; continue;
    }
    if (line.startsWith('# ')) {
      nodes.push(<h1 key={i} className="text-2xl font-black text-white mt-4 mb-4">{line.slice(2)}</h1>);
      i++; continue;
    }

    // HR
    if (line === '---') {
      nodes.push(<hr key={i} className="border-slate-700 my-6" />);
      i++; continue;
    }

    // Unordered list
    if (line.startsWith('- ')) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push(lines[i].slice(2));
        i++;
      }
      nodes.push(
        <ul key={i} className="list-disc list-inside space-y-1 my-3 text-slate-300 text-sm">
          {listItems.map((item, li) => <li key={li}>{renderInline(item)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list (1. 2. 3. ...)
    if (/^\d+\. /.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        listItems.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      nodes.push(
        <ol key={i} className="list-decimal list-inside space-y-1 my-3 text-slate-300 text-sm">
          {listItems.map((item, li) => <li key={li}>{renderInline(item)}</li>)}
        </ol>
      );
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i++; continue;
    }

    // Paragraph
    nodes.push(
      <p key={i} className="text-slate-300 text-sm leading-relaxed my-2">
        {renderInline(line)}
      </p>
    );
    i++;
  }

  return nodes;
}

function renderInline(text: string): React.ReactNode[] {
  // **bold**, `code`, basic text
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="text-xs bg-slate-800 text-cyan-300 px-1.5 py-0.5 rounded">{part.slice(1, -1)}</code>;
    }
    return part;
  });
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const KnowledgeArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading, isError } = useQuery({
    queryKey: ['knowledge-article', slug],
    queryFn: () => getArticleBySlug(slug!),
    enabled: !!slug,
    staleTime: 10 * 60 * 1000, // 10 minutes — article content rarely changes
  });


  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-cyan-400" />
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-400 gap-4">
        <BookOpen size={40} className="text-slate-600" />
        <p className="text-lg">Article not found.</p>
        <Link to="/knowledge" className="text-cyan-400 hover:text-cyan-300 text-sm">
          ← Back to Knowledge Hub
        </Link>
      </div>
    );
  }

  const updatedDate = new Date(article.updated_at).toLocaleDateString('en-ZA', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {article && (
        <SEO
          title={article.title}
          description={`${article.title} — My Investments knowledge base article.`}
          noIndex
        />
      )}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Back link */}
        <Link
          to="/knowledge"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={15} />
          Knowledge Hub
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-full px-3 py-1 font-medium">
              {article.category}
            </span>
          </div>
          <h1 className="text-3xl font-black text-white leading-tight mb-4">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <Calendar size={12} />
              Updated {updatedDate}
            </span>
            {article.tags.length > 0 && (
              <span className="flex items-center gap-1.5">
                <Tag size={12} />
                {article.tags.join(', ')}
              </span>
            )}
          </div>
        </div>

        {/* Article body */}
        <div className="prose-invert max-w-none">
          {renderMarkdown(article.content_md)}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-800 flex items-center justify-between">
          <Link
            to="/knowledge"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Knowledge Hub
          </Link>
          <Link
            to="/settings/mcp"
            className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            Go to MCP Settings →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeArticle;
